import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { CloudOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyIssuesState } from "@/components/issues/empty-issues-state/empty-issues-state";
import { IssueItem } from "@/components/issues/issue-item/issue-item";
import { IssueListFrame } from "@/components/issues/issue-list/issue-list-frame";
import { useIssueRows } from "@/components/issues/issue-list/use-issue-rows";
import {
  IssueTableHeader,
  IssueTableRow,
} from "@/components/issues/issue-table-row/issue-table-row";
import type { IssueStatusAccessFor } from "@/hooks/use-issue-status-access";
import { OfflineError } from "@/lib/api";
import type { IssueOrdering } from "@/lib/query-keys";
import type { ProjectIssue } from "@/types/api";

interface IssuesViewProps {
  projectId: string;
  issues: ProjectIssue[];
  isLoading: boolean;
  error: Error | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  onRetry: () => void;
  /** True when any filter beyond the status selection is applied. */
  filtered: boolean;
  onClearFilters: () => void;
  ordering: IssueOrdering;
  onOrderingChange: (ordering: IssueOrdering) => void;
  /**
   * Open an issue in place instead of navigating — the page's desktop side
   * panel. Omitted where the list should link to each issue's page.
   */
  onOpenIssue?: (issue: ProjectIssue) => void;
  /** The issue open in the side panel, marked in the list. */
  activeIssueId?: string | null;
  /**
   * Render the two-line cards even when the window is wide enough for the
   * table — the side panel leaves the list too narrow for six columns.
   */
  forceCards?: boolean;
  /**
   * What this viewer may do to each issue's status. When it returns access,
   * the row offers the update sheet — a swipe on a phone, the status pill on
   * desktop. Omitted, no row offers it. See [[issues-view.spec]].
   */
  statusAccessFor?: IssueStatusAccessFor;
  /**
   * Selection mode: every row ticks instead of opening, and the update
   * shortcuts are off. The page owns the ticks. See [[issues-view.spec]].
   */
  selection?: IssueListSelection;
  /**
   * The viewer sees only the issues they're on: the list says so under its
   * last row, and its empty state stops inviting them to raise one. See
   * [[issues-page.spec]] § Only the issues you're on.
   */
  onlyYours?: boolean;
}

/** What the page hands the list while ticking issues for a report. */
export interface IssueListSelection {
  selectedIds: ReadonlySet<string>;
  onToggle: (issue: ProjectIssue) => void;
}

export function IssuesView({
  projectId,
  issues,
  isLoading,
  error,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onRetry,
  filtered,
  onClearFilters,
  ordering,
  onOrderingChange,
  onOpenIssue,
  activeIssueId = null,
  forceCards = false,
  statusAccessFor,
  selection,
  onlyYours = false,
}: IssuesViewProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Table or cards, the status shortcut and its sheet: shared with an issues
  // report. See [[issue-list-frame.spec]].
  const { asTable, updateStatusVia, updateHandlerFor, statusSheet } =
    useIssueRows({
      projectId,
      statusAccessFor,
      selecting: !!selection,
      forceCards,
    });
  const selectionFor = (issue: ProjectIssue) =>
    selection
      ? {
          selected: selection.selectedIds.has(issue.id),
          onToggle: () => selection.onToggle(issue),
        }
      : undefined;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
      },
      // Start the next page before the sentinel is on screen, so scrolling
      // stays continuous rather than stalling at the bottom of each page.
      { rootMargin: "400px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className="px-4">
        <div className="flex flex-col gap-px overflow-hidden rounded-[10px] border border-border">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-[76px] w-full rounded-none" />
          ))}
        </div>
      </div>
    );
  } else if (error) {
    // Issues are not cached for offline use in this wave, so a lost connection
    // is an expected state rather than a failure. Saying "couldn't load" here
    // would read as a bug and send someone hunting for one.
    const offline = error instanceof OfflineError;
    body = (
      <div className="flex h-full w-full items-center justify-center px-10 py-12">
        <div className="flex max-w-sm flex-col items-center gap-4 text-center">
          {offline && (
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-muted">
              <CloudOff className="h-12 w-12 text-muted-foreground" />
            </div>
          )}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">
              {offline ? "Issues need a connection" : "Couldn't load issues"}
            </h3>
            <p className="text-sm text-muted-foreground">
              {offline
                ? "Photos work offline, but issues don't yet. They'll load when you're back on a signal."
                : "Something went wrong loading this project's issues."}
            </p>
          </div>
          {!offline && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          )}
        </div>
      </div>
    );
  } else if (issues.length === 0) {
    body = (
      <EmptyIssuesState
        filtered={filtered}
        onClearFilters={onClearFilters}
        onlyYours={onlyYours}
      />
    );
  } else {
    // Only the first card that can swipe plays the daily peek. A viewer who
    // may not update the top issue still gets it on the first they may.
    const swipeHintId =
      !asTable && updateStatusVia === "swipe" && !selection
        ? issues.find((issue) => updateHandlerFor(issue))?.id
        : undefined;
    body = (
      <div className="px-4 pb-4">
        <IssueListFrame>
          {asTable && (
            <IssueTableHeader
              ordering={ordering}
              onOrderingChange={onOrderingChange}
              selecting={!!selection}
            />
          )}
          {issues.map((issue) =>
            asTable ? (
              <IssueTableRow
                key={issue.id}
                projectId={projectId}
                issue={issue}
                onOpen={onOpenIssue}
                onUpdateStatus={updateHandlerFor(issue)}
                selection={selectionFor(issue)}
              />
            ) : (
              <IssueItem
                key={issue.id}
                projectId={projectId}
                issue={issue}
                onOpen={onOpenIssue}
                isActive={!selection && issue.id === activeIssueId}
                onUpdateStatus={updateHandlerFor(issue)}
                updateStatusVia={updateStatusVia}
                swipeHint={issue.id === swipeHintId}
                selection={selectionFor(issue)}
              />
            ),
          )}
          {isFetchingNextPage && (
            <Skeleton className="h-[76px] w-full rounded-none" />
          )}
        </IssueListFrame>
        <div ref={sentinelRef} aria-hidden="true" />
        {/* After the last page only: earlier, more rows are still coming. */}
        {onlyYours && !hasNextPage && (
          <p className="pt-3 text-center text-xs text-muted-foreground">
            Only issues you&apos;re on show here.
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      {body}
      {statusSheet}
    </>
  );
}
